import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { notifyError, notifySucces } from "../../utils/notifications";
import useCheckPermission from "../../utils/useCheckPermission";

const usePermissionsManagement = () => {
  const [permissions, setPermissions] = useState({
    installment: { dock: false, cards: false },
    retroactive: { dock: false, cards: false },
    antecipateSales: { dock: false, cards: false },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    key: keyof typeof permissions;
    type: "dock" | "cards";
  } | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPermissions, setIsLoadingPermissions] =
    useState<boolean>(false);

  const slugToKey: Record<string, keyof typeof permissions> = {
    use_custom_initial_value_in_installment: "installment",
    permission_launch_adjustment_with_previous_date: "retroactive",
    permission_antecipate_sales: "antecipateSales",
  };

  const keyToSlug: Record<keyof typeof permissions, string> = {
    installment: "use_custom_initial_value_in_installment",
    retroactive: "permission_launch_adjustment_with_previous_date",
    antecipateSales: "permission_antecipate_sales",
  };

  const fetchPermissionsManagement = useCallback(async () => {
    setIsLoadingPermissions(true);
    try {
      const response = await api.get("permissions_by_company");

      const apiData = response.data;
      const currentProcessor = response.current_processor;

      if (Array.isArray(apiData)) {
        const newPermissions = {
          installment: { dock: false, cards: false },
          retroactive: { dock: false, cards: false },
          antecipateSales: { dock: false, cards: false },
        };

        apiData.forEach((item) => {
          const stateKey = slugToKey[item.slug];
          if (stateKey) {
            newPermissions[stateKey] = {
              dock: !!item.is_dock,
              cards: !!item.is_cards,
            };
          }
        });

        setPermissions(newPermissions);
        const cleanToStore = JSON.parse(
          JSON.stringify({ apiData, currentProcessor })
        );
        sessionStorage.setItem(
          "@SAC_RPE_PERMISSION_MANAGEMENT",
          JSON.stringify(cleanToStore)
        );
      }
    } catch (error) {
      console.error("Erro ao buscar permissões", error);
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissionsManagement();
  }, [fetchPermissionsManagement]);

  const openConfirmation = (
    key: keyof typeof permissions,
    type: "dock" | "cards"
  ) => {
    setPendingChange({ key, type });
    setIsModalOpen(true);
  };

  const confirmChange = async () => {
    if (!pendingChange) return;

    const { key, type } = pendingChange;
    const newValue = !permissions[key][type];
    const slug = keyToSlug[key];

    try {
      setIsLoading(true);
      await api.patch("permissions_management", {
        processor: type,
        feature: slug,
        value: newValue,
      });

      await fetchPermissionsManagement();
      notifySucces("Permissão atualizada com sucesso", true);
    } catch (error) {
      console.error("Erro ao atualizar permissão", error);
      notifyError("Erro ao atualizar permissão", "", false, true);
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
      setPendingChange(null);
    }
  };

  const cancelChange = () => {
    setIsModalOpen(false);
    setPendingChange(null);
  };

  return {
    permissions,
    isModalOpen,
    openConfirmation,
    confirmChange,
    cancelChange,
    fetchPermissionsManagement,
    isLoading,
    isLoadingPermissions,
  };
};

export default usePermissionsManagement;
